import type { ContratCxpiDTO } from '@/features/api/extranet/dto/Demande/CXPI'
import type { NaturesFichiersDemandeCallback } from '@/features/domaine/regles-akto/shared'
import { isNotNullUndefinedOrEmptyString } from '@/features/shared/tools'

/* Natures fichiers CxPI */
export const ContratTypeLettreMission = 'ContratTypeLettreMission' as const
export const ReconnaissanceTravailleurHandicapé = 'ReconnaissanteQTH' as const
export const CharteEngagementMissionJeune = 'CharteEngagementMissionJeune' as const
// export const ProgrammeFormationExterne = 'ProgrammeFormationExterne' as const

/* Natures fichiers DRF */
export const Convention = 'Convention' as const
export const ConventionBilanEvaluation = 'ConventionBilanEvaluation' as const
export const ConventionBilanPositionnement = 'ConventionBilanPositionnement' as const
export const ProtocoleFormationAFEST = 'ProtocoleFormationAFEST' as const
export const Programme = 'Programme' as const
export const EngagementParties = 'EngagementParties' as const

export function getStatModule(demande: ContratCxpiDTO) {
  return (demande.formation?.modules ?? []).reduce<{
    nb: number
    nbExternes: number
    existBilanEval: boolean
    existBilanPos: boolean
    nbAFEST: number
    nbEntrepriseUtilisatrice: number
  }>(
    (stat, module) => {
      stat.nb++
      stat.nbExternes += module.typeModule === 'Externe' ? 1 : 0
      stat.existBilanEval = stat.existBilanEval || module.typeModule === 'BilanEvaluation'
      stat.existBilanPos = stat.existBilanPos || module.typeModule === 'BilanPositionnement'
      stat.nbAFEST += module.modalitePedagogique === 'Afest' ? 1 : 0
      stat.nbEntrepriseUtilisatrice += module.formationRealiseeDansEntreprise === true ? 1 : 0
      return stat
    },
    {
      nb: 0,
      nbExternes: 0,
      existBilanEval: false,
      existBilanPos: false,
      nbAFEST: 0,
      nbEntrepriseUtilisatrice: 0,
    },
  )
}

export const getNaturesFichiersCxpi: NaturesFichiersDemandeCallback<ContratCxpiDTO> = (demande, _typeContext) => {
  const statModule = getStatModule(demande)

  if (demande.typeDemande === 'Formation') return getFichiersDemandeFormation(demande, statModule)
  if ('code' in demande && isNotNullUndefinedOrEmptyString(demande.code))
    return getFichiersDossierDrf(demande, statModule)
  else return getFichiersDemandeDrf(demande, statModule)
}

function getFichiersDemandeFormation(demande: ContratCxpiDTO, _statModule: ReturnType<typeof getStatModule>) {
  const obligatoires: Set<string> = new Set([ContratTypeLettreMission])
  const facultatifs = new Set(['Autre'])
  const facultatifsDefaut: Set<string> = new Set()
  const nombreFichiersMaximum: Record<string, number> = {}
  const nombreFichiersMinimum: Record<string, number> = {}

  // PRT-1520 : dépôt facultatif (simplification administrative gestion)
  if (demande.salarie?.beneficieReconnaissanceTravailleurHandicape) {
    facultatifs.add(ReconnaissanceTravailleurHandicapé)
  }

  if (demande.salarie?.contratPacea || demande.salarie?.contratEngagementJeune) {
    facultatifs.add(CharteEngagementMissionJeune)
  }

  return {
    obligatoires: [...obligatoires],
    facultatifs: [...facultatifs],
    facultatifsDefaut: [...facultatifsDefaut],
    nombreFichiersMaximum,
    nombreFichiersMinimum,
  }
}

function getFichiersDemandeDrf(_demande: ContratCxpiDTO, statModule: ReturnType<typeof getStatModule>) {
  const obligatoires: Set<string> = new Set()
  const facultatifs = new Set([Convention, 'Autre'])
  const facultatifsDefaut: Set<string> = new Set()
  const nombreFichiersMaximum: Record<string, number> = {}
  if (statModule.nbEntrepriseUtilisatrice > 0) {
    obligatoires.add(Programme)
    obligatoires.add(EngagementParties)
  } else {
    facultatifs.add(Programme)
  }

  if (statModule.nbAFEST > 0) {
    facultatifs.add(ProtocoleFormationAFEST)
  }

  if (statModule.existBilanPos) {
    facultatifs.add(ConventionBilanPositionnement)
  }

  if (statModule.existBilanEval) {
    facultatifs.add(ConventionBilanEvaluation)
  }

  return {
    obligatoires: [...obligatoires],
    facultatifs: [...facultatifs],
    facultatifsDefaut: [...facultatifsDefaut],
    nombreFichiersMaximum,
    nombreFichiersMinimum: {
      [Programme]: statModule.nbExternes,
      ...(statModule.nbAFEST > 0 ? { [ProtocoleFormationAFEST]: statModule.nbAFEST } : {}),
    },
    messagesAvertissement: {
      [Programme]: '(un programme par module externe)',
      [Convention]: '(une convention par module externe)',
      ...(statModule.nbAFEST > 0 ? { [ProtocoleFormationAFEST]: '(un protocole AFEST par module externe)' } : {}),
    },
  }
}

function getFichiersDossierDrf(_demande: ContratCxpiDTO, statModule: ReturnType<typeof getStatModule>) {
  const obligatoires: Set<string> = new Set([Convention, Programme])
  const facultatifs: Set<string> = new Set()
  const facultatifsDefaut: Set<string> = new Set()

  if (statModule.nbEntrepriseUtilisatrice > 0) {
    obligatoires.add(EngagementParties)
  }

  if (statModule.nbAFEST > 0) {
    obligatoires.add(ProtocoleFormationAFEST)
  }

  if (statModule.existBilanPos) {
    obligatoires.add(ConventionBilanPositionnement)
  }

  if (statModule.existBilanEval) {
    obligatoires.add(ConventionBilanEvaluation)
  }

  return {
    obligatoires: [...obligatoires],
    facultatifs: [...facultatifs],
    facultatifsDefaut: [...facultatifsDefaut],
    nombreFichiersMaximum: {},
    nombreFichiersMinimum: {
      [Convention]: statModule.nbExternes,
      [Programme]: statModule.nbExternes,
      ...(statModule.nbAFEST > 0 ? { [ProtocoleFormationAFEST]: statModule.nbAFEST } : {}),
    },
    messagesAvertissement: {
      [Programme]: '(un programme par module externe)',
      [Convention]: '(une convention par module externe)',
      ...(statModule.nbAFEST > 0 ? { [ProtocoleFormationAFEST]: '(un protocole AFEST par module externe)' } : {}),
    },
  }
}
